import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Archive,
  Delete,
  Edit,
  Check,
  Close,
  DeleteSweep,
} from '@mui/icons-material';
import { useChatMemory } from '../context/ChatMemoryContext';
import { useActiveConversationIds } from '../assistant/assistantTurns';
import { formatDistanceToNow } from 'date-fns';

/**
 * Chat conversation sidebar component.
 * @param {object} props - Component props.
 * @param {() => void} props.onNewChat - Callback after "New chat" (the thread is created on the first message).
 * @param {(conversationId: string) => void} [props.onSelectConversation] - Callback after a conversation is opened from the list.
 * @returns {JSX.Element} Conversation sidebar.
 */
function ChatConversationSidebar({ onNewChat, onSelectConversation }) {
  const {
    conversations,
    currentConversation,
    loadConversation,
    clearCurrentConversation,
    updateConversationTitle,
    archiveConversation,
    deleteConversation,
    deleteConversationsBulk,
    loading,
  } = useChatMemory();
  // Conversations Inky is working in. Changes only when that set changes, never per streamed token.
  const workingIds = useActiveConversationIds();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  /**
   * Handle conversation selection.
   * @param {string} conversationId - Conversation ID.
   * @returns {Promise<void>} Completion promise.
   */
  const handleSelectConversation = async (conversationId) => {
    try {
      await loadConversation(conversationId);
      if (onSelectConversation) onSelectConversation(conversationId);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  /**
   * Start a new chat. The conversation row is created with the first message, so an
   * abandoned "New chat" never leaves an empty thread in History.
   * @returns {void}
   */
  const handleNewChat = () => {
    clearCurrentConversation();
    if (onNewChat) onNewChat();
  };

  /**
   * Handle opening context menu.
   * @param {Event} event - Click event.
   * @param {string} conversationId - Conversation ID.
   * @returns {void}
   */
  const handleOpenMenu = (event, conversationId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedConvId(conversationId);
  };

  /**
   * Handle closing context menu.
   * @returns {void}
   */
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedConvId(null);
  };

  /**
   * Handle starting title edit.
   * @param {object} conversation - Conversation object.
   * @returns {void}
   */
  const handleStartEdit = (conversation) => {
    setEditingId(conversation.id);
    setEditTitle(conversation.title);
    handleCloseMenu();
  };

  /**
   * Handle saving title edit.
   * @returns {Promise<void>} Completion promise.
   */
  const handleSaveEdit = async () => {
    if (!editingId || !editTitle.trim()) return;

    try {
      await updateConversationTitle(editingId, editTitle.trim());
      setEditingId(null);
      setEditTitle('');
    } catch (error) {
      console.error('Failed to update title:', error);
    }
  };

  /**
   * Handle canceling title edit.
   * @returns {void}
   */
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
  };

  /**
   * Handle archiving conversation.
   * @returns {Promise<void>} Completion promise.
   */
  const handleArchive = async () => {
    if (!selectedConvId) return;

    const idToArchive = selectedConvId;
    handleCloseMenu();

    try {
      await archiveConversation(idToArchive);
    } catch (error) {
      console.error('Failed to archive conversation:', error);
    }
  };

  /**
   * Handle deleting conversation.
   * @returns {Promise<void>} Completion promise.
   */
  const handleDelete = async () => {
    if (!selectedConvId) return;

    if (window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      const idToDelete = selectedConvId;
      handleCloseMenu();

      try {
        await deleteConversation(idToDelete);
      } catch (error) {
        console.error('Failed to delete conversation:', error);
      }
    }
  };

  /**
   * Toggle selection mode for bulk delete.
   * @returns {void}
   */
  const handleToggleSelectionMode = () => {
    setSelectionMode((prev) => !prev);
    setSelectedIds(new Set());
  };

  /**
   * Toggle selection of a conversation.
   * @param {string} conversationId - Conversation ID.
   * @returns {void}
   */
  const handleToggleSelect = (conversationId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(conversationId)) {
        next.delete(conversationId);
      } else {
        next.add(conversationId);
      }
      return next;
    });
  };

  /**
   * Select or deselect all conversations.
   * @param {boolean} selectAll - If true, select all; if false, clear selection.
   * @returns {void}
   */
  const handleSelectAll = (selectAll) => {
    if (selectAll) {
      setSelectedIds(new Set(conversations.map((c) => c.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  /**
   * Handle bulk delete of selected conversations.
   * @returns {Promise<void>} Completion promise.
   */
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const message = ids.length === 1
      ? 'Are you sure you want to delete this conversation? This action cannot be undone.'
      : `Are you sure you want to delete ${ids.length} conversations? This action cannot be undone.`;

    if (window.confirm(message)) {
      try {
        await deleteConversationsBulk(ids);
        setSelectedIds(new Set());
        setSelectionMode(false);
      } catch (error) {
        console.error('Failed to bulk delete conversations:', error);
      }
    }
  };

  /**
   * Format timestamp relative to now.
   * @param {string} timestamp - ISO timestamp.
   * @returns {string} Formatted time.
   */
  const formatTime = (timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (error) {
      return '';
    }
  };

  /**
   * Truncate text to length.
   * @param {string} text - Text to truncate.
   * @param {number} maxLength - Maximum length.
   * @returns {string} Truncated text.
   */
  const truncate = (text, maxLength = 40) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<Add />}
            onClick={handleNewChat}
            disabled={loading}
          >
            New Chat
          </Button>
          <Tooltip title={selectionMode ? 'Done selecting' : 'Select chats to delete'}>
            <span>
              <IconButton
                color={selectionMode ? 'primary' : 'default'}
                onClick={handleToggleSelectionMode}
                disabled={conversations.length === 0}
              >
                <DeleteSweep />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {selectionMode && selectedIds.size > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={handleBulkDelete}
            >
              Delete {selectedIds.size} selected
            </Button>
            <Button size="small" variant="text" onClick={() => handleSelectAll(false)}>
              Clear
            </Button>
          </Box>
        )}
        {selectionMode && (
          <Button size="small" variant="text" onClick={() => handleSelectAll(true)} sx={{ mb: 1 }}>
            Select all
          </Button>
        )}
        <Typography variant="caption" color="text.secondary">
          {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Conversation List */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {conversations.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No conversations with Inky yet.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {conversations.map((conv) => (
              <ListItem
                key={conv.id}
                disablePadding
                secondaryAction={
                  selectionMode ? (
                    <Checkbox
                      edge="end"
                      size="small"
                      checked={selectedIds.has(conv.id)}
                      onChange={() => handleToggleSelect(conv.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => handleOpenMenu(e, conv.id)}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  )
                }
              >
                <ListItemButton
                  selected={!selectionMode && currentConversation?.id === conv.id}
                  onClick={() => selectionMode ? handleToggleSelect(conv.id) : handleSelectConversation(conv.id)}
                  sx={{
                    mx: 0.75,
                    my: 0.25,
                    borderRadius: 'var(--q-radius-md, 8px)',
                    '&.Mui-selected': { bgcolor: 'var(--q-accent-soft, #ECE8FC)' },
                    '&.Mui-selected:hover': { bgcolor: 'var(--q-accent-soft, #E7E3FA)' },
                  }}
                >
                  <ListItemText
                    primary={
                      editingId === conv.id ? (
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEdit();
                              if (e.key === 'Escape') handleCancelEdit();
                            }}
                            autoFocus
                            fullWidth
                            variant="standard"
                          />
                          <IconButton size="small" onClick={handleSaveEdit}>
                            <Check fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={handleCancelEdit}>
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Typography variant="body2" noWrap>
                          {truncate(conv.title)}
                        </Typography>
                      )
                    }
                    secondary={
                      workingIds.has(conv.id) ? (
                        <Typography variant="caption" sx={{ color: 'var(--q-accent, #4F3BD6)' }}>
                          Inky is working…
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(conv.updated_at)}
                        </Typography>
                      )
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem
          onClick={() => {
            const conv = conversations.find(c => c.id === selectedConvId);
            if (conv) handleStartEdit(conv);
          }}
        >
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Rename
        </MenuItem>
        <MenuItem onClick={handleArchive}>
          <Archive fontSize="small" sx={{ mr: 1 }} />
          Archive
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default ChatConversationSidebar;
